/**
 * VisibilityBadge — marks a file that is PRIVATE to the arche that created it.
 *
 * `visibility` decides whether a file is part of the user's own files (other
 * arches can be given it, it can be shared and attached) or private to one
 * arche. It is set once, when the file is created, and never changes — a move
 * is never a hidden change of visibility (docs/vault/arche-files-policy.md).
 * It is still shown, because it answers a question the user will ask: "why
 * can't I move this file, or attach it to an email?"
 *
 * Renders NOTHING for an ordinary file (`user-vault`). Same rule as
 * `FolderExposureBadge`: a badge on every row carries no signal, so only the
 * exceptional state is marked. `compact` draws just the lock, for places as
 * small as a Desktop icon; it carries the same explanation.
 */
import { Lock, PencilLine } from 'lucide-react';
import type { VaultDocumentDto } from '@/lib/api';

interface VisibilityBadgeProps {
  visibility: VaultDocumentDto['visibility'];
  className?: string;
  /** Only the lock (e.g. on a Desktop icon). */
  compact?: boolean;
}

const DETAIL = 'Private to the arche that made it — it stays in its My-Fias folder';

/**
 * The explanation, for assistive tech. An `aria-label` on a bare `<span>` names
 * nothing — a generic element takes no accessible name — and where a screen
 * reader does honour it, it REPLACES the visible text. A visually-hidden span
 * is read after the visible word instead; `title` carries the same sentence
 * for pointer users.
 */
function Detail({ children }: { children: string }) {
  return <span className="sr-only"> — {children}</span>;
}

export function VisibilityBadge({ visibility, className = '', compact }: VisibilityBadgeProps) {
  if (visibility !== 'arche-private') return null;
  if (compact) {
    return (
      <span
        className={`inline-flex items-center justify-center rounded-full border border-border bg-muted p-0.5 text-muted-foreground ${className}`}
        title={DETAIL}
      >
        <Lock className="h-3 w-3" aria-hidden="true" />
        <span className="sr-only">Arche-private — {DETAIL}</span>
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1 rounded border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground ${className}`}
      title={DETAIL}
    >
      <Lock className="h-3 w-3" aria-hidden="true" />
      Arche-private
      <Detail>{DETAIL}</Detail>
    </span>
  );
}

/** The amber "an app can change this" treatment, light and dark — shared so
 *  the file list, the file's page and Arche Access mark it the same way. */
export const AMBER_BADGE_CLASSES =
  'border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200';

const EDITED_LABEL = 'An arche last changed this file — open it to see which, and what it can do';

/**
 * EditedByAppBadge — marks a file whose content was last changed by an APP
 * (under edit access the user gave it), rather than by a person.
 *
 * The list cannot name the app cheaply (that needs a lookup per row), and does
 * not need to: the row's job is to say "an app has been writing here", and the
 * file's detail page names it and offers the way out. Renders nothing for a
 * file a person last changed, or that was never edited in place.
 */
export function EditedByAppBadge({
  lastWrittenByArcheId,
  className = '',
}: {
  lastWrittenByArcheId?: string | null;
  className?: string;
}) {
  if (!lastWrittenByArcheId) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${AMBER_BADGE_CLASSES} ${className}`}
      title={EDITED_LABEL}
    >
      <PencilLine className="h-3 w-3" aria-hidden="true" />
      Edited by an arche
      <Detail>{EDITED_LABEL}</Detail>
    </span>
  );
}
